import ReadMore from './read-more';
import { loadRecaptcha } from '../captcha/google';
import { yieldToMainThread } from '../yield';
import triggerEvent, { GAEventName } from '../analytics/ga/events';
import { ErrorType, ErrorEvent } from '../analytics/ga/events';

function showErrInContainer( htmlContainer: HTMLElement, errorString: string ): void {
    const msg = errorString || 'Sorry, we are unable to handle your request';
    const errorHTML = '<div class="alert alert-warning">' + msg + '</div>';
    htmlContainer.innerHTML = errorHTML + htmlContainer.innerHTML;
}

type TranslateDescriptionResponse = {
    success: boolean;
    content: string;
    language: string;
    errors?: Record<string, string>
}

const captchaAction = 'translate';

function initPropertyDescriptionHandlers( container: HTMLElement | Document ): void {
    const transSelect = container.querySelector<HTMLSelectElement>( '[name=select-trans-lang]' );

    if ( !transSelect ) {
        return;
    }

    const descsContainer = container.querySelector( '.descriptions' );

    if ( !descsContainer ) {
        return;
    }

    const handleShowInLinks = function handleShowInLinks(): void {
        container.querySelectorAll( '.descriptions a[data-show-in]' ).forEach( ( el: HTMLAnchorElement ): void => {
            el.addEventListener( 'click', ( event: Event ) => {
                event.preventDefault();
                event.stopPropagation();
                transSelect.value = ( event.target as HTMLElement ).dataset.showIn || '';
                transSelect.dispatchEvent( new Event( 'change', { bubbles: true } ) );
            } );
        } );
    };

    const addNewDescription = function addNewDescription( propertyId: string, lang: string, result: string, langName: string ): void {
        descsContainer.querySelectorAll( '.description' ).forEach( ( el: HTMLElement ) => el.classList.add( 'd-none' ) );

        // add if option does not exists
        let option = transSelect.querySelector<HTMLOptionElement>( 'option[value=\'' + lang + '\']' );

        if ( !option ) {
            option = document.createElement( 'option' );
            option.value = lang;
            option.innerHTML = langName;
            option.dataset.propertyId = propertyId;
            transSelect.appendChild( option );
        }

        transSelect.value = lang;

        const range = document.createRange();
        range.selectNode( descsContainer );

        descsContainer.appendChild( range.createContextualFragment( result ) );

        const propertyDescription = document.querySelector<HTMLElement>( `[data-desc-lang="${lang}"] > p.property-description` );

        if ( !propertyDescription ) {
            return;
        }

        new ReadMore( propertyDescription, {
            readMoreText: propertyDescription.dataset.tsReadMore,
            textLimit: 600,
        } );

        handleShowInLinks();
    };

    const loadDescription = async function(): Promise<void> {
        descsContainer.classList.add( 'descriptions--loading' );

        const selectedOpt = transSelect.options[ transSelect.selectedIndex ];

        const lang = selectedOpt.value;
        const selector = `[data-desc-lang=${lang}]`;

        let found = false;

        descsContainer.querySelectorAll( '.description' ).forEach( ( el: HTMLElement ) => {
            if ( el.matches( selector ) ) {
                el.classList.remove( 'd-none' );
                found = true;
            } else {
                el.classList.add( 'd-none' );
            }
        } );

        if ( found ) {
            descsContainer.classList.remove( 'descriptions--loading' );
            return;
        }

        const propertyId = selectedOpt.dataset.propertyId || '';
        const preview = selectedOpt.dataset.inPreview ? 1 : 0;

        const url = new URL( window.location.origin + '/load-property-description' );
        url.searchParams.set( 'id', propertyId );
        url.searchParams.set( 'requestedLanguage', lang );
        url.searchParams.set( 'preview', preview.toString() );

        await yieldToMainThread();

        const response = await window.http( url );

        if ( !response.ok ) {
            throw new Error( 'failed getting description translation for: ' + propertyId );
        }

        const result = await response.json();

        addNewDescription( propertyId, lang, result.content, result.language );

        descsContainer.classList.remove( 'descriptions--loading' );
    };

    transSelect.addEventListener( 'change', async ( event: Event ) => {
        event.preventDefault();
        event.stopPropagation();
        loadDescription();
    } );

    const transLinks = container.querySelectorAll( '.descriptions [data-translate]' );

    transLinks.forEach( async function( el: HTMLElement ): Promise<void> {
        descsContainer.classList.add( 'descriptions--loading' );

        el.classList.add( 'd-none' );

        const sourceLanguage = el.dataset.source;
        const targetLanguage = el.dataset.lang;
        const propertyId = el.dataset.propertyId;

        const url = new URL( window.location.origin + '/can-translate-description' );
        url.searchParams.set( 'id', propertyId || '' );
        url.searchParams.set( 'sourceLanguage', sourceLanguage || '' );
        url.searchParams.set( 'targetLanguage', targetLanguage || '' );

        await yieldToMainThread();

        const response = await window.http( url );

        if ( !response.ok ) {
            throw new Error( 'failed getting description translation response' );
        }

        const result = await response.json();

        if ( !result.data ) {
            return;
        }

        el.classList.remove( 'd-none' );

        descsContainer.classList.remove( 'descriptions--loading' );
    } );

    transLinks.forEach( function( el: HTMLElement ): void {
        el.addEventListener( 'click', async function( event: Event ): Promise<void> {
            descsContainer.classList.add( 'descriptions--loading' );

            const token = await loadRecaptcha( captchaAction );

            const descLink = event.target as HTMLElement | null;

            if ( !descLink ) {
                return;
            }

            descLink.classList.add( 'd-none' );
            const lang = descLink.dataset.lang as string;
            const propertyId = descLink.dataset.propertyId as string;

            const url = new URL( window.location.origin + descLink.dataset.url );

            const formData =  new FormData();
            formData.append( 'token', token );
            formData.append( 'action', captchaAction );

            try {
                await yieldToMainThread();

                const response = await window.http( url, {
                    method: 'POST',
                    body: formData,
                } );

                const result = await response.json() as TranslateDescriptionResponse;

                if ( !result.success ) {
                    throw new Error( ( result.errors && result.errors.form ) ? result.errors.form as string : 'description translation failed' );
                }

                addNewDescription( propertyId, lang, result.content, result.language );
            } catch ( err ) {
                const translateBtn = document.querySelector( '.translate-btn' );

                if ( translateBtn && translateBtn.parentElement ) {
                    showErrInContainer( translateBtn.parentElement, err.message );
                }

                if ( err instanceof Error ) {
                    triggerEvent<ErrorEvent>( GAEventName.Error, {
                        'error_type': ErrorType.ErrServer,
                        'error_message': err.message,
                    } );
                }
            }

            descsContainer.classList.remove( 'descriptions--loading' );

            descLink.classList.add( 'd-none' );

            return;
        } );
    } );

    handleShowInLinks();
}

export default initPropertyDescriptionHandlers;
